import { Spinner, TextInput } from '@palmetto/palmetto-components';
import { useCallback } from 'react';
import type PlacesAutocompleteType from 'react-places-autocomplete';
import type { Suggestion } from 'react-places-autocomplete';
import _PlacesAutocompleteMod from 'react-places-autocomplete';

import { useGooglePlacesReady } from '../../helpers/googlePlacesReady';

import styles from './AddressInput.module.scss';

// Vite 8 pre-bundles this CJS module without unwrapping __esModule, so the default
// import resolves to the full exports object. Unwrap it here to get the component.
const PlacesAutocomplete = ((_PlacesAutocompleteMod as any).default ??
  _PlacesAutocompleteMod) as typeof PlacesAutocompleteType;

interface AddressInputProps {
  id: string;
  label: string;
  className?: string;
  name: string;
  onChange: (value: string) => void;
  onSelect: (address: string, placeId: string) => void;
  error?: string | null;
  isDisabled?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onError?: (status: string, clearSuggestion: () => void) => void;
  value?: string;
  [key: string]: any;
}

const AddressInput = ({
  onBlur,
  onFocus,
  isDisabled = false,
  error = null,
  name,
  onSelect,
  onChange,
  onError,
  value = '',
  ...restProps
}: AddressInputProps) => {
  const handleError = (status: string, clearSuggestion: () => void) => {
    if (onError) onError(status, clearSuggestion);
  };

  const placesReady = useGooglePlacesReady();

  const handleManualChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value),
    [onChange],
  );

  // Until the Places library is attached, fall back to a plain text input so
  // the field still accepts manual entry. Rendering PlacesAutocomplete early
  // leaves autocompleteService undefined and every keystroke throws.
  if (!placesReady) {
    return (
      <div className={styles.autoCompleteRoot}>
        <TextInput
          name={name}
          value={value}
          onChange={handleManualChange}
          onBlur={onBlur}
          onFocus={onFocus}
          isDisabled={isDisabled}
          error={error}
          {...restProps}
        />
      </div>
    );
  }

  return (
    <PlacesAutocomplete
      onChange={onChange}
      onSelect={onSelect}
      value={value}
      onError={handleError}
      shouldFetchSuggestions={value?.length > 2}
    >
      {({ getInputProps, getSuggestionItemProps, suggestions, loading }) => {
        const autocompleteInputProps = getInputProps({ name });

        return (
          <div className={styles.autoCompleteRoot}>
            <TextInput
              {...autocompleteInputProps}
              onBlur={onBlur}
              onFocus={onFocus}
              isDisabled={isDisabled}
              error={error}
              {...restProps}
            />
            {(suggestions.length > 0 || loading) && (
              <div className={`${styles?.autoCompleteDropdownContainer} text-light`}>
                {loading && (
                  <div className={styles?.autoCompleteSpinnerContainer}>
                    <Spinner />
                  </div>
                )}
                {suggestions.map((suggestion: Suggestion) => {
                  const suggestionClassName = suggestion.active
                    ? styles.autoCompleteSuggestionActive
                    : styles.autoCompleteSuggestion;
                  return (
                    <div
                      {...getSuggestionItemProps(suggestion)}
                      key={suggestion.placeId}
                      className={suggestionClassName}
                    >
                      <span>{suggestion.description}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }}
    </PlacesAutocomplete>
  );
};

export default AddressInput;
